import React from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Stack from '@mui/material/Stack'
import CaretDownSvg from '../../assets/images/CaretDown.svg'
import ActivityTypeField from './ActivityTypeField'
import ActivityPopulationField from './ActivityPopulationField'
import ActivityIntendedUseField from './ActivityIntendedUseField'
import ActivityCountriesField from './ActivityCountriesField'
import { requiredFieldHelperText } from './requiredFieldHelperText'

interface ActivityDetailsFieldProps {
    activityType: string
    onActivityTypeChange: (value: string) => void
    population: string
    onPopulationChange: (value: string) => void
    dataSource: string
    onDataSourceChange: (value: string) => void
    link: string
    onLinkChange: (value: string) => void
    anticipatedMessaging: string
    onAnticipatedMessagingChange: (value: string) => void
    outcomesEndpoints: string
    onOutcomesEndpointsChange: (value: string) => void
    intendedUse: Record<string, boolean>
    onIntendedUseChange: (key: string, checked: boolean) => void
    countries: string[]
    onCountriesChange: (value: string[]) => void
    compoundName: string[]
    onCompoundNameChange: (value: string[]) => void
    indication: string
    onIndicationChange: (value: string) => void
    genericName: string[]
    onGenericNameChange: (value: string[]) => void
    // Edit-only: highlights key market countries
    keyMarketNames?: string[]
    disabled?: boolean
    existingActivityType?: string
    existingPopulation?: string
    // Keyed by the org's required-fields config (e.g. 'type', 'compoundName')
    requiredFields?: string[]
    fieldErrors?: Record<string, string>
}

const ActivityDetailsField: React.FC<ActivityDetailsFieldProps> = ({
    activityType,
    onActivityTypeChange,
    population,
    onPopulationChange,
    dataSource,
    onDataSourceChange,
    link,
    onLinkChange,
    anticipatedMessaging,
    onAnticipatedMessagingChange,
    outcomesEndpoints,
    onOutcomesEndpointsChange,
    intendedUse,
    onIntendedUseChange,
    countries,
    onCountriesChange,
    compoundName,
    onCompoundNameChange,
    indication,
    onIndicationChange,
    genericName,
    onGenericNameChange,
    keyMarketNames,
    disabled,
    existingActivityType,
    existingPopulation,
    requiredFields,
    fieldErrors,
}) => {
    const [expanded, setExpanded] = React.useState(true)

    // Auto-expand this section when a failed submit flags one of its fields, so a missing required
    // field can't hide inside a collapsed section. Keyed on a stable boolean so the effect only
    // fires on the false→true transition — it won't re-open a section the user collapses while the
    // error is still unresolved.
    const hasMissingField = [
        'type',
        'population',
        'dataSource',
        'link',
        'anticipatedMessaging',
        'outcomesEndpoints',
        'countries',
        'indication',
        'genericName',
        'compoundName',
    ].some((f) => !!fieldErrors?.[f])
    React.useEffect(() => {
        if (hasMissingField) setExpanded(true)
    }, [hasMissingField])

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            disableGutters
            elevation={0}
            sx={{ backgroundColor: 'transparent', '&:before': { display: 'none' } }}
        >
            <AccordionSummary
                expandIcon={
                    <Box
                        component="img"
                        src={CaretDownSvg}
                        sx={{
                            width: 20,
                            height: 20,
                            transition: 'transform 0.2s',
                            transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)',
                        }}
                    />
                }
                sx={{
                    px: 0,
                    py: 1,
                    backgroundColor: 'inherit',
                    '& .MuiAccordionSummary-expandIconWrapper': { transform: 'rotate(0deg)' },
                    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'rotate(180deg)' },
                }}
            >
                <Typography variant="18x600">Details</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0, pt: 1 }}>
                <Stack spacing={2}>
                    <Box sx={{ width: '50%' }}>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Activity Type
                            {requiredFields?.includes('type') && (
                                <Typography component="span" sx={{ color: 'error.main' }}>
                                    {' '}
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <ActivityTypeField
                            value={activityType}
                            onChange={onActivityTypeChange}
                            disabled={disabled}
                            existingValue={existingActivityType}
                            error={!!fieldErrors?.type}
                            helperText={fieldErrors?.type}
                            required={requiredFields?.includes('type')}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                                Population
                                {requiredFields?.includes('population') && (
                                    <Typography component="span" sx={{ color: 'error.main' }}>
                                        {' '}
                                        *
                                    </Typography>
                                )}
                            </Typography>
                            <ActivityPopulationField
                                value={population}
                                onChange={onPopulationChange}
                                disabled={disabled}
                                existingValue={existingPopulation}
                                error={!!fieldErrors?.population}
                                helperText={fieldErrors?.population}
                                required={requiredFields?.includes('population')}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                                Data Source
                                {requiredFields?.includes('dataSource') && (
                                    <Typography component="span" sx={{ color: 'error.main' }}>
                                        {' '}
                                        *
                                    </Typography>
                                )}
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter Data Source"
                                value={dataSource}
                                error={!!fieldErrors?.dataSource}
                                helperText={requiredFieldHelperText('dataSource', requiredFields, fieldErrors)}
                                onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                onChange={(e) => onDataSourceChange(e.target.value)}
                                sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                            />
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Link
                            {requiredFields?.includes('link') && (
                                <Typography component="span" sx={{ color: 'error.main' }}>
                                    {' '}
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter Link"
                            value={link}
                            error={!!fieldErrors?.link}
                            helperText={requiredFieldHelperText('link', requiredFields, fieldErrors)}
                            onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                            onChange={(e) => onLinkChange(e.target.value)}
                            sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Anticipated Messaging
                            {requiredFields?.includes('anticipatedMessaging') && (
                                <Typography component="span" sx={{ color: 'error.main' }}>
                                    {' '}
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Enter Anticipated Messaging"
                            value={anticipatedMessaging}
                            error={!!fieldErrors?.anticipatedMessaging}
                            helperText={requiredFieldHelperText('anticipatedMessaging', requiredFields, fieldErrors)}
                            onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                            onChange={(e) => onAnticipatedMessagingChange(e.target.value)}
                            sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Outcomes/Endpoints
                            {requiredFields?.includes('outcomesEndpoints') && (
                                <Typography component="span" sx={{ color: 'error.main' }}>
                                    {' '}
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Enter Outcomes/Endpoints"
                            value={outcomesEndpoints}
                            error={!!fieldErrors?.outcomesEndpoints}
                            helperText={requiredFieldHelperText('outcomesEndpoints', requiredFields, fieldErrors)}
                            onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                            onChange={(e) => onOutcomesEndpointsChange(e.target.value)}
                            sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                        />
                    </Box>

                    <Box>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Intended Use
                        </Typography>
                        <ActivityIntendedUseField
                            value={intendedUse}
                            onChange={(key, checked) => onIntendedUseChange(key, checked)}
                        />
                    </Box>

                    <Box>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Countries
                            {requiredFields?.includes('countries') && (
                                <Typography component="span" sx={{ color: 'error.main' }}>
                                    {' '}
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <ActivityCountriesField
                            value={countries}
                            onChange={onCountriesChange}
                            disabled={disabled}
                            error={!!fieldErrors?.countries}
                            helperText={requiredFieldHelperText('countries', requiredFields, fieldErrors)}
                            keyMarketNames={keyMarketNames}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                                Indication
                                {requiredFields?.includes('indication') && (
                                    <Typography component="span" sx={{ color: 'error.main' }}>
                                        {' '}
                                        *
                                    </Typography>
                                )}
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter Indication"
                                value={indication}
                                disabled={disabled}
                                error={!!fieldErrors?.indication}
                                helperText={requiredFieldHelperText('indication', requiredFields, fieldErrors)}
                                onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                onChange={(e) => onIndicationChange(e.target.value)}
                                sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                                Generic Name
                                {requiredFields?.includes('genericName') && (
                                    <Typography component="span" sx={{ color: 'error.main' }}>
                                        {' '}
                                        *
                                    </Typography>
                                )}
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="Enter Generic Name (comma-separated)"
                                value={genericName.join(', ')}
                                disabled={disabled}
                                error={!!fieldErrors?.genericName}
                                helperText={requiredFieldHelperText('genericName', requiredFields, fieldErrors)}
                                onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                onChange={(e) =>
                                    onGenericNameChange(
                                        e.target.value ? e.target.value.split(',').map((s) => s.trim()) : []
                                    )
                                }
                                sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ width: '50%' }}>
                        <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                            Compound
                            {requiredFields?.includes('compoundName') && (
                                <Typography component="span" sx={{ color: 'error.main' }}>
                                    {' '}
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter Compound (comma-separated)"
                            value={compoundName.join(', ')}
                            disabled={disabled}
                            error={!!fieldErrors?.compoundName}
                            helperText={requiredFieldHelperText('compoundName', requiredFields, fieldErrors)}
                            onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                            onChange={(e) =>
                                onCompoundNameChange(
                                    e.target.value ? e.target.value.split(',').map((s) => s.trim()) : []
                                )
                            }
                            sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                        />
                    </Box>
                </Stack>
            </AccordionDetails>
        </Accordion>
    )
}

export default ActivityDetailsField
